import React, { useState } from "react";
import { Card, Divider, Row, Col, Tooltip, Badge } from "antd";
import { BranchesOutlined, UserAddOutlined } from "@ant-design/icons";
import {
  Flex,
  Text,
  Image,
  SendIcon,
  Button,
  SpeakerPersonIcon,
  WhiteboardIcon,
  ShiftActivityIcon,
} from "@fluentui/react-northstar";
import defaultSchoolLogo from "../../../../Assets/images/DefaultSchoolLogo.png";
import { useTrustedNetwork } from "../../../../context/TrustedNetworkContext";
import { SpinWheelIcon } from "../../../../Assets/icons/icons";
import { useTranslation } from "react-i18next";
import { HandshakeOutlined, SchoolOutlined } from "@mui/icons-material";
import ConfirmationDialog from "../Common/ConfirmationDialog";

const iconStyle = {
  color: "#6264a7",
  fontSize: "clamp(12px, 1.6vw, 16px)",
};

const metricCountStyle = {
  fontWeight: 700,
  fontSize: "clamp(12px, 1.8vw, 16px)",
  color: "#1a1a1a",
  lineHeight: 1.2,
};

const metricLabelStyle = {
  fontSize: "clamp(8px, 1vw, 10px)",
  color: "#6b7280",
  fontWeight: 500,
  marginTop: 1,
  textAlign: "center",
};

const metricBoxStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "6px 2px",
  borderRadius: 6,
  background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
  border: "1px solid #e2e8f0",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
  transition: "all 0.2s ease",
  cursor: "default",
  minHeight: 55,
};

const metricBoxHoverStyle = {
  ...metricBoxStyle,
  transform: "translateY(-1px)",
  boxShadow: "0 4px 12px rgba(98, 100, 167, 0.15)",
};

const zeroValueStyle = {
  ...metricCountStyle,
  color: "#9ca3af",
  fontWeight: 500,
};

const SchoolCard = ({ school }) => {
  const { t } = useTranslation();
  const translation = t("trustedNetwork").schools;
  const schoolCardTranslation = translation.schoolCard;

  const { generateTrustRequest } = useTrustedNetwork();
  const [isGeneratingRequest, setIsGeneratingRequest] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    targetSchoolId: null,
  });

  const {
    School,
    ContactEmail,
    SchoolLogo,
    OwnStgUrl,
    teachersCount = 0,
    collectionsCount = 0,
    spinWheelsCount = 0,
  } = school;

  const studentsCount =
    school?.studentsCount ?? school?.totalStudents ?? school?.studentCount ?? 0;

  const branchesCount =
    school?.branchesCount ??
    school?.totalBranches ??
    (Array.isArray(school?.branches) ? school.branches.length : 0) ??
    0;

  const [logo, setLogo] = useState(() => {
    if (!SchoolLogo) return null;
    if (SchoolLogo.includes("?")) return SchoolLogo;

    const sasToken = process.env.REACT_APP_AZURE_SAS_TOKEN?.replace(/"/g, "");
    return sasToken ? SchoolLogo + sasToken : SchoolLogo;
  });

  const handleTrustRequest = (targetSchoolId) => {
    setConfirmDialog({
      open: true,
      targetSchoolId: targetSchoolId,
    });
  };

  const handleConfirmRequest = async () => {
    setIsGeneratingRequest(true);
    setConfirmDialog({ open: false, targetSchoolId: null });
    await generateTrustRequest(confirmDialog.targetSchoolId);
    setIsGeneratingRequest(false);
  };

  const handleCancelRequest = () => {
    setConfirmDialog({ open: false, targetSchoolId: null });
  };

  const metrics = [
    {
      icon: <SchoolOutlined style={iconStyle} />,
      label: schoolCardTranslation?.students || "Students",
      count: studentsCount,
      emoji: "🎓",
      tooltip: schoolCardTranslation?.studentsTooltip || "Number of students",
    },
    {
      icon: <SpeakerPersonIcon style={iconStyle} />,
      label: schoolCardTranslation?.teachers || "Teachers",
      count: teachersCount,
      emoji: "👨‍🏫",
      tooltip: schoolCardTranslation?.teachersTooltip || "Number of teachers",
    },
    {
      icon: <BranchesOutlined style={iconStyle} />,
      label: schoolCardTranslation?.branches || "Branches",
      count: branchesCount,
      emoji: "🏢",
      tooltip: schoolCardTranslation?.branchesTooltip || "Number of branches",
    },
    {
      icon: <WhiteboardIcon style={iconStyle} />,
      label: schoolCardTranslation?.collections || "Collections",
      count: collectionsCount,
      emoji: "📚",
      tooltip:
        schoolCardTranslation?.collectionsTooltip || "Number of collections",
    },
    {
      icon: (
        <div
          style={{
            width: "clamp(16px, 2.2vw, 20px)",
            height: "clamp(16px, 2.2vw, 20px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {SpinWheelIcon("#6264a7")}
        </div>
      ),
      label: schoolCardTranslation?.spinWheels || "Spinwheels",
      count: spinWheelsCount,
      emoji: "🎯",
      tooltip:
        schoolCardTranslation?.spinWheelsTooltip || "Number of spin wheels",
    },
  ];

  const [hovered, setHovered] = useState(false);
  const [metricHovered, setMetricHovered] = useState(null);

  const getStatusBadge = () => {
    if (school.requested) {
      return (
        <Badge
          count={schoolCardTranslation?.requested || "Requested"}
          style={{
            backgroundColor: "#e6e6f9",
            color: "#6f71c2",
            fontSize: "10px",
            fontWeight: 600,
            borderRadius: "12px",
            padding: "2px 8px",
            height: "auto",
            lineHeight: "16px",
          }}
        />
      );
    }
    return (
      <Badge
        count={schoolCardTranslation?.available || "Available"}
        style={{
          backgroundColor: "#e6f4ea",
          color: "#237a57",
          fontSize: "10px",
          fontWeight: 600,
          borderRadius: "12px",
          padding: "2px 8px",
          height: "auto",
          lineHeight: "16px",
        }}
      />
    );
  };

  return (
    <Card
      hoverable
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: 18,
        border: "1px solid #e2e8f0",
        boxShadow: hovered
          ? "0 20px 40px rgba(98, 100, 167, 0.15), 0 0 0 1px rgba(98, 100, 167, 0.2)"
          : "0 4px 16px rgba(98, 100, 167, 0.08)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minWidth: 320,
        maxWidth: 380,
      }}
      bodyStyle={{
        padding: 0,
        flex: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top Section - School Info */}
      <div
        style={{
          padding: "16px 20px 12px 20px",
          background: "linear-gradient(135deg, #6264a7 0%, #5a5c9e 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background Pattern */}
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "100px",
            height: "100px",
            background:
              "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
            borderRadius: "50%",
            transform: "translate(30px, -30px)",
          }}
        />

        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 16,
            position: "relative",
          }}
        >
          {/* Avatar with Status Ring */}
          <div style={{ position: "relative", flexShrink: 0 }}>
            <div
              style={{
                width: 45,
                height: 45,
                borderRadius: "50%",
                background: "#ffffff",
                border: school.requested
                  ? "3px solid #a3a5d9" // 🟣 Requested
                  : "3px solid #3bb77e", // ✅ Available
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.10)", // Softer shadow
                transition: "all 0.3s ease", // Smooth hover/interaction
              }}
            >
              {logo ? (
                <Image
                  src={logo}
                  alt={`${School} Logo`}
                  style={{
                    width: "90%",
                    height: "90%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#6264a7",
                    fontWeight: 700,
                    fontSize: "22px",
                    background:
                      "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                  }}
                >
                  {School?.charAt(0)?.toUpperCase()}
                </div>
              )}
            </div>
            {/* Status Indicator Dot */}
            <div
              style={{
                position: "absolute",
                bottom: 1,
                right: 1,
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: school.requested ? "#a3a5d9" : "#3bb77e",
                border: "2px solid white",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
              }}
            />
          </div>

          {/* School Info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                marginBottom: 6,
              }}
            >
              <Text
                content={School}
                weight="bold"
                title={School}
                style={{
                  fontSize: "clamp(16px, 3vw, 16px)",
                  color: "#ffffff",
                  lineHeight: 1.3,
                  wordBreak: "break-word",
                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                }}
              />
              {getStatusBadge()}
            </div>
            {ContactEmail && (
              <div
                style={{
                  fontSize: "clamp(11px, 1.5vw, 12px)",
                  color: "rgba(255, 255, 255, 0.9)",
                  wordBreak: "break-all",
                  lineHeight: 1.4,
                }}
              >
                {ContactEmail}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Middle Section - Metrics */}
      <div style={{ padding: "14px 16px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 4,
          }}
        >
          {metrics.map((metric, index) => (
            <Tooltip
              key={index}
              title={metric.tooltip || `${metric.label}: ${metric.count}`}
            >
              <div
                style={
                  metricHovered === index ? metricBoxHoverStyle : metricBoxStyle
                }
                onMouseEnter={() => setMetricHovered(index)}
                onMouseLeave={() => setMetricHovered(null)}
              >
                <div style={{ marginBottom: 4 }}>{metric.icon}</div>
                <span
                  style={metric.count === 0 ? zeroValueStyle : metricCountStyle}
                >
                  {metric.count}
                </span>
                <div style={metricLabelStyle}>{metric.label}</div>
              </div>
            </Tooltip>
          ))}
        </div>
      </div>

      {/* Bottom Section - Action Button */}
      <div
        style={{
          padding: "0 20px 20px 20px",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-end",
          flex: 1,
        }}
      >
        {school.requested ? (
          <Button
            size="small"
            icon={<ShiftActivityIcon size="small" />}
            disabled
            content={schoolCardTranslation?.requested || "Request Sent"}
            style={{
              width: "auto",
              minWidth: "fit-content",
              height: 32,
              borderRadius: 12,
              backgroundColor: "#f3f4f6",
              color: "#6b7280",
              fontSize: "12px",
              fontWeight: 600,
              border: "1px solid #e5e7eb",
              cursor: "not-allowed",
              padding: "0 12px",
            }}
          />
        ) : (
          <Button
            size="small"
            icon={
              !isGeneratingRequest && (
                <HandshakeOutlined style={{ fontSize: 14 }} />
              )
            }
            disabled={isGeneratingRequest}
            loading={isGeneratingRequest}
            onClick={() => handleTrustRequest(OwnStgUrl)}
            content={
              isGeneratingRequest
                ? `${schoolCardTranslation?.requesting || "Connecting"}...`
                : schoolCardTranslation?.request || "Connect"
            }
            style={{
              width: "auto",
              minWidth: "fit-content",
              height: 32,
              borderRadius: 12,
              fontSize: "12px",
              fontWeight: 600,
              background: isGeneratingRequest
                ? "linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)"
                : "linear-gradient(135deg, #6264a7 0%, #5a5c9e 100%)",
              color: "#ffffff",
              border: "none",
              cursor: isGeneratingRequest ? "not-allowed" : "pointer",
              boxShadow: isGeneratingRequest
                ? "none"
                : "0 2px 8px rgba(98, 100, 167, 0.3)",
              transition: "all 0.2s ease",
              padding: "0 12px",
            }}
            onMouseEnter={(e) => {
              if (!isGeneratingRequest) {
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow = "0 4px 12px rgba(98, 100, 167, 0.4)";
              }
            }}
            onMouseLeave={(e) => {
              if (!isGeneratingRequest) {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 2px 8px rgba(98, 100, 167, 0.3)";
              }
            }}
          />
        )}
      </div>

      <ConfirmationDialog
        open={confirmDialog.open}
        onConfirm={handleConfirmRequest}
        onCancel={handleCancelRequest}
        title={
          schoolCardTranslation?.connectConfirmTitle || "Connect to School"
        }
        content={`${
          schoolCardTranslation?.connectConfirmMessage ||
          "Are you sure you want to send a connection request to"
        } ${School}?`}
        confirmButton={{
          content: schoolCardTranslation?.connect || "Connect",
          primary: true,
          disabled: isGeneratingRequest,
        }}
        cancelButton={schoolCardTranslation?.cancel || "Cancel"}
      />
    </Card>
  );
};

export default SchoolCard;
